import React, { useState, useEffect } from "react";
import { useAuth } from "../../../contexts/AuthContext";
import { studentService } from "../../../services/studentService";
import { getUnpostedStatementTuition } from "../../../services/feeService";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";

const FeePayment = ({ onClose, studentData, onPaymentComplete }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [billingRecords, setBillingRecords] = useState([]);
  const [statementRecords, setStatementRecords] = useState([]);
  const [unpostedTuition, setUnpostedTuition] = useState([]);
  const [selectedBill, setSelectedBill] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("mpesa");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [activeTab, setActiveTab] = useState("outstanding");

  useEffect(() => {
    loadBillingData();
  }, [user?.id, studentData?.id, studentData?.profile_id]);

  const loadBillingData = async () => {
    if (!user?.id) return;

    setLoading(true);
    try {
      const billingStudentIds = [...new Set([
        studentData?.profile_id,
        user?.id,
        studentData?.id,
      ].filter(Boolean))];
      const billingResults = await Promise.all(
        billingStudentIds.map((studentId) => studentService.getStudentBilling(studentId)),
      );
      const billingError = billingResults.find((result) => result?.error)?.error;
      if (billingError) throw billingError;

      const records = billingResults
        .flatMap((result) => result?.data || [])
        .filter((record, index, all) => !record?.id || all.findIndex((item) => item?.id === record.id) === index);
      setBillingRecords(records);

      const statementStudentIds = [...new Set([
        studentData?.id,
        studentData?.profile_id,
        user?.id,
      ].filter(Boolean))];
      const statementResults = await Promise.all(
        statementStudentIds.map((studentId) => studentService.getStudentStatement(studentId)),
      );
      const statementRows = statementResults
        .flatMap((result) => result?.data || [])
        .filter((record, index, all) => !record?.id || all.findIndex((item) => item?.id === record.id) === index);
      setStatementRecords(statementRows);
      const pendingTuition = studentData
        ? await getUnpostedStatementTuition(studentData)
        : [];
      setUnpostedTuition(pendingTuition || []);

      // Auto-select first outstanding bill
      const outstandingBill = records.find((bill) =>
        ["pending", "overdue", "partial"]?.includes(bill?.payment_status),
      );
      if (outstandingBill) {
        setSelectedBill(outstandingBill);
        const balance =
          parseFloat(outstandingBill?.amount_ksh || 0) -
          parseFloat(outstandingBill?.paid_amount_ksh || 0);
        setPaymentAmount(balance?.toFixed(2));
      }
    } catch (error) {
      setError("Failed to load billing information. Please try again.");
      console.error("Billing data error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async () => {
    const bill =
      selectedBill ||
      billingRecords.find((item) =>
        ["pending", "overdue", "partial"].includes(item?.payment_status),
      );
    if (!bill || !paymentAmount || parseFloat(paymentAmount) <= 0) {
      setError("Please select a bill and enter a valid payment amount.");
      return;
    }

    if (paymentMethod === "mpesa" && !phoneNumber) {
      setError("Please enter your M-Pesa phone number.");
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      // Simulate payment processing
      const paymentReference = `PAY${Date.now()}`;
      const checkoutRequestId =
        selectedBill?.ecitizen_request_id ||
        selectedBill?.request_id ||
        paymentReference;
      window.location.assign(
        `https://payments.ecitizen.go.ke/checkout?request_id=${encodeURIComponent(checkoutRequestId)}`,
      );
      return;

      // In a real implementation, you would integrate with payment gateways here
      // For now, we'll simulate a successful payment
      await new Promise((resolve) => setTimeout(resolve, 2000));

      const { data, error } = await studentService?.processPayment(
        bill.id,
        parseFloat(paymentAmount),
        paymentReference,
      );

      if (error) throw error;

      setSuccess(
        `Payment of KSh ${parseFloat(paymentAmount)?.toLocaleString()} processed successfully! Reference: ${paymentReference}`,
      );

      // Reload billing data
      await loadBillingData();

      // Notify parent component
      onPaymentComplete?.();

      // Reset form
      setPaymentAmount("");
      setPhoneNumber("");
      setSelectedBill(null);
    } catch (error) {
      setError(
        error?.message || "Payment processing failed. Please try again.",
      );
      console.error("Payment error:", error);
    } finally {
      setProcessing(false);
    }
  };

  const getOutstandingBills = () => {
    return billingRecords?.filter((bill) =>
      ["pending", "overdue", "partial"]?.includes(bill?.payment_status),
    );
  };

  const getPaidBills = () => {
    return billingRecords?.filter((bill) => bill?.payment_status === "paid");
  };

  const getBillBalance = (bill) => {
    return (
      parseFloat(bill?.amount_ksh || 0) - parseFloat(bill?.paid_amount_ksh || 0)
    );
  };
  const statementRows = [...statementRecords, ...unpostedTuition];
  const statementBalance = statementRows.length
    ? statementRows.reduce((balance, row) => (
      balance + (Number(row.debit_amount) || 0) - (Number(row.credit_amount) || 0)
    ), 0)
    : null;
  const billingBalance = billingRecords.reduce((sum, bill) => {
    const recordedTotal = Number(bill.total_amount || 0);
    const billed = recordedTotal > 0
      ? recordedTotal
      : Number(bill.base_tuition_fee || bill.amount_ksh || 0)
        + Number(bill.summative_exam_fee || 0)
        + Number(bill.statutory_fee || 0)
        + Number(bill.school_fee || 0)
        + Number(bill.late_reporting_fee || 0)
        + Number(bill.late_unit_registration_fee || 0)
        + Number(bill.reactivation_fee || 0);
    const paid = Number(bill.paid_amount_ksh || bill.amount_paid || 0);
    return sum + Math.max(0, billed - paid);
  }, 0);
  const totalFeeBalance = Math.max(0, statementBalance ?? billingBalance);

  const formatCurrency = (amount) => {
    return `KSh ${parseFloat(amount || 0)?.toLocaleString()}`;
  };

  const getBillStatusColor = (status) => {
    switch (status) {
      case "paid":
        return "text-success";
      case "overdue":
        return "text-error";
      case "partial":
        return "text-warning";
      default:
        return "text-muted-foreground";
    }
  };

  const getBillStatusBadge = (bill) => {
    const status = bill?.payment_status;
    const isOverdue =
      new Date(bill?.due_date) < new Date() && status !== "paid";

    if (isOverdue && status !== "paid")
      return { text: "Overdue", color: "bg-error text-error-foreground" };
    if (status === "paid")
      return { text: "Paid", color: "bg-success text-success-foreground" };
    if (status === "partial")
      return { text: "Partial", color: "bg-warning text-warning-foreground" };
    return { text: "Pending", color: "bg-muted text-muted-foreground" };
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-card border border-border rounded-lg p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <Icon
              name="Loader2"
              size={32}
              className="animate-spin text-primary mb-4"
            />
            <p className="text-muted-foreground">Loading fee information...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!loading)
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
        <div className="w-full max-w-4xl rounded-lg border bg-white shadow-xl">
          <div className="flex items-center justify-between border-b px-6 py-4">
            <h2 className="text-2xl font-normal">Pesa Flow Checkout</h2>
            <button onClick={onClose} aria-label="Close" className="text-3xl leading-none text-slate-700">×</button>
          </div>
          <div className="grid gap-4 p-10 md:grid-cols-[160px_1fr] md:items-center">
            <label className="text-right font-semibold">Fee Balance</label>
            <input
              readOnly
              value={totalFeeBalance.toFixed(2)}
              className="h-14 border bg-slate-100 px-4"
            />
            <label className="text-right font-semibold">Amount To Pay</label>
            <input
              type="number"
              min="1"
              value={paymentAmount}
              onChange={(e) => setPaymentAmount(e.target.value)}
              className="h-14 border px-4"
            />
            <div></div>
            <Button
              onClick={handlePayment}
              disabled={
                !paymentAmount || parseFloat(paymentAmount) <= 0 || processing
              }
              className="w-fit bg-emerald-600"
            >
              {processing ? "Processing…" : "Pay Securely"}
            </Button>
          </div>
        </div>
      </div>
    );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-foreground">
              Pesa Flow Checkout
            </h2>
            <p className="text-sm text-muted-foreground">
              View your current fee balance and pay securely through eCitizen
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Alert Messages */}
          {error && (
            <div className="bg-error/10 border border-error/20 rounded-lg p-4 flex items-center">
              <Icon name="AlertCircle" size={20} className="text-error mr-3" />
              <p className="text-error">{error}</p>
            </div>
          )}

          {success && (
            <div className="bg-success/10 border border-success/20 rounded-lg p-4 flex items-center">
              <Icon
                name="CheckCircle"
                size={20}
                className="text-success mr-3"
              />
              <p className="text-success">{success}</p>
            </div>
          )}

          {/* Tabs */}
          <div className="flex space-x-1 bg-muted p-1 rounded-lg">
            {false && (
              <button
                onClick={() => setActiveTab("outstanding")}
                className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${
                  activeTab === "outstanding"
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                Outstanding Fees
              </button>
            )}
            {false && (
              <button
                onClick={() => setActiveTab("history")}
                className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${
                  activeTab === "history"
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                Payment History
              </button>
            )}
          </div>

          {activeTab === "outstanding" && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Outstanding Bills */}
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-4">
                  Fee Balance
                </h3>
                <div className="space-y-3">
                  {getOutstandingBills()?.length > 0 ? (
                    getOutstandingBills()?.map((bill) => {
                      const balance = getBillBalance(bill);
                      const badge = getBillStatusBadge(bill);
                      const isSelected = selectedBill?.id === bill?.id;

                      return (
                        <div
                          key={bill?.id}
                          onClick={() => {
                            setSelectedBill(bill);
                            setPaymentAmount(balance?.toFixed(2));
                          }}
                          className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                            isSelected
                              ? "border-primary bg-primary/5"
                              : "border-border hover:border-primary/50"
                          }`}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <h4 className="font-medium text-foreground">
                                {bill?.billing_config?.billing_type
                                  ?.replace("_", " ")
                                  ?.toUpperCase()}{" "}
                                Fee
                              </h4>
                              <p className="text-sm text-muted-foreground">
                                Due:{" "}
                                {new Date(bill?.due_date)?.toLocaleDateString()}
                              </p>
                            </div>
                            <span
                              className={`px-2 py-1 text-xs font-medium rounded-full ${badge?.color}`}
                            >
                              {badge?.text}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <div className="text-sm">
                              <p className="text-muted-foreground">
                                Total: {formatCurrency(bill?.amount_ksh)}
                              </p>
                              {parseFloat(bill?.paid_amount_ksh || 0) > 0 && (
                                <p className="text-muted-foreground">
                                  Paid: {formatCurrency(bill?.paid_amount_ksh)}
                                </p>
                              )}
                            </div>
                            <div className="text-right">
                              <p
                                className={`font-semibold ${getBillStatusColor(bill?.payment_status)}`}
                              >
                                {formatCurrency(balance)}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                Balance
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-center py-8">
                      <Icon
                        name="CreditCard"
                        size={48}
                        className="text-success mx-auto mb-4"
                      />
                      <p className="text-muted-foreground">
                        No outstanding fees. You're all up to date!
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Payment Form */}
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-4">
                  Pay via eCitizen
                </h3>
                {selectedBill ? (
                  <div className="space-y-4">
                    {/* Selected Bill Summary */}
                    <div className="bg-muted/50 border border-border rounded-lg p-4">
                      <h4 className="font-medium text-foreground mb-2">
                        Payment Details
                      </h4>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">
                            Bill Type:
                          </span>
                          <span className="text-foreground">
                            {selectedBill?.billing_config?.billing_type
                              ?.replace("_", " ")
                              ?.toUpperCase()}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">
                            Total Amount:
                          </span>
                          <span className="text-foreground">
                            {formatCurrency(selectedBill?.amount_ksh)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">
                            Amount Paid:
                          </span>
                          <span className="text-foreground">
                            {formatCurrency(selectedBill?.paid_amount_ksh)}
                          </span>
                        </div>
                        <div className="flex justify-between font-medium">
                          <span className="text-foreground">Balance Due:</span>
                          <span className="text-foreground">
                            {formatCurrency(getBillBalance(selectedBill))}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Payment Amount */}
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Payment Amount (KSh)
                      </label>
                      <Input
                        type="number"
                        value={paymentAmount}
                        onChange={(e) => setPaymentAmount(e?.target?.value)}
                        placeholder="Enter amount"
                        min="1"
                        max={getBillBalance(selectedBill)}
                        step="0.01"
                      />
                    </div>

                    {/* Payment Method */}
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Payment Method
                      </label>
                      <div className="space-y-2">
                        {["mpesa", "card", "bank"]?.map((method) => (
                          <label
                            key={method}
                            className="flex items-center space-x-3 cursor-pointer"
                          >
                            <input
                              type="radio"
                              name="paymentMethod"
                              value={method}
                              checked={paymentMethod === method}
                              onChange={(e) =>
                                setPaymentMethod(e?.target?.value)
                              }
                              className="text-primary"
                            />
                            <span className="text-foreground capitalize">
                              {method === "mpesa"
                                ? "M-Pesa"
                                : method === "card"
                                  ? "Credit/Debit Card"
                                  : "Bank Transfer"}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Phone Number for M-Pesa */}
                    {paymentMethod === "mpesa" && (
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                          M-Pesa Phone Number
                        </label>
                        <Input
                          type="tel"
                          value={phoneNumber}
                          onChange={(e) => setPhoneNumber(e?.target?.value)}
                          placeholder="254XXXXXXXXX"
                          pattern="[0-9]{12}"
                        />
                      </div>
                    )}

                    {/* Payment Button */}
                    <Button
                      onClick={handlePayment}
                      disabled={
                        processing ||
                        !paymentAmount ||
                        parseFloat(paymentAmount) <= 0
                      }
                      className="w-full"
                      iconName={processing ? "Loader2" : "CreditCard"}
                      iconClassName={processing ? "animate-spin" : ""}
                    >
                      {processing
                        ? "Processing Payment..."
                        : `Pay ${formatCurrency(paymentAmount)}`}
                    </Button>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Icon
                      name="MousePointer"
                      size={48}
                      className="text-muted-foreground mx-auto mb-4"
                    />
                    <p className="text-muted-foreground">
                      Select a bill from the left to make a payment
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === "history" && (
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-4">
                Payment History
              </h3>
              <div className="space-y-3">
                {getPaidBills()?.length > 0 ? (
                  getPaidBills()?.map((bill) => (
                    <div
                      key={bill?.id}
                      className="border border-border rounded-lg p-4"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <h4 className="font-medium text-foreground">
                            {bill?.billing_config?.billing_type
                              ?.replace("_", " ")
                              ?.toUpperCase()}{" "}
                            Fee
                          </h4>
                          <p className="text-sm text-muted-foreground">
                            Paid:{" "}
                            {new Date(bill?.paid_at)?.toLocaleDateString()} •
                            Ref: {bill?.payment_reference}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-success">
                            {formatCurrency(bill?.paid_amount_ksh)}
                          </p>
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-success text-success-foreground">
                            Paid
                          </span>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <Icon
                      name="Receipt"
                      size={48}
                      className="text-muted-foreground mx-auto mb-4"
                    />
                    <p className="text-muted-foreground">
                      No payment history available
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FeePayment;
